import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Mail, Phone, LogOut, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: user?.name || '', phone: user?.phone || '' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/profile', form);
      toast.success('Profil mis a jour');
    } catch {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <div className="max-w-lg mx-auto px-4 md:px-8 py-8 fade-in" data-testid="profile-page">
      <h1 className="text-3xl font-light tracking-tight mb-8" style={{ fontFamily: 'Outfit' }}>Mon Profil</h1>

      <div className="bg-[#121212] border border-white/5 rounded-2xl p-6 space-y-5">
        <div className="flex items-center gap-4 pb-5 border-b border-white/5">
          <div className="w-14 h-14 rounded-full bg-[#D4AF37]/15 flex items-center justify-center">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-14 h-14 rounded-full object-cover" />
            ) : (
              <User className="w-6 h-6 text-[#D4AF37]" />
            )}
          </div>
          <div>
            <div className="font-semibold">{user?.name}</div>
            <div className="text-xs text-[#A1A1AA]">{user?.email}</div>
          </div>
        </div>

        <div>
          <Label className="text-xs text-[#A1A1AA] mb-1.5 block">Nom</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
            <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              data-testid="profile-name" className="pl-10 bg-[#0A0A0A] border-white/10 focus:border-[#D4AF37] rounded-xl" />
          </div>
        </div>

        <div>
          <Label className="text-xs text-[#A1A1AA] mb-1.5 block">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
            <Input value={user?.email || ''} disabled
              className="pl-10 bg-[#0A0A0A] border-white/10 rounded-xl opacity-50" />
          </div>
        </div>

        <div>
          <Label className="text-xs text-[#A1A1AA] mb-1.5 block">Telephone</Label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
            <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
              data-testid="profile-phone" placeholder="+33 ..."
              className="pl-10 bg-[#0A0A0A] border-white/10 focus:border-[#D4AF37] rounded-xl" />
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <Button onClick={handleSave} disabled={saving} data-testid="profile-save-btn"
            className="bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full px-6 flex-1">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
          <Button onClick={handleLogout} variant="outline" data-testid="profile-logout-btn"
            className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-full px-6">
            <LogOut className="w-4 h-4 mr-2" /> Deconnexion
          </Button>
        </div>
      </div>
    </div>
  );
}
